import {
  CreateQuestionReviewTaskAndSubtasksErrorCode,
  EntityType,
  useCreateQuestionReviewTaskMutation,
  WorkflowCode,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import { useAuth } from '@shared/auth';
import { Button, ConfirmationModal, Flex } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useLogger } from '@shared/logger';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import {
  QuestionReviewTaskSubmissionErrorModal,
  type QuestionReviewTaskSubmissionErrorModalContent,
} from '../../components';
import {
  useQuestionReviewTaskCreationFormContext,
  useQuestionReviewTaskCreationSelectedRowsContext,
} from '../../providers';
import { removeItemSetQuestionIds } from '../add-question-section/question-row-mappers';

const SUBMISSION_ERROR_CONTENT_MAP: Partial<
  Record<
    CreateQuestionReviewTaskAndSubtasksErrorCode,
    QuestionReviewTaskSubmissionErrorModalContent
  >
> = {
  [CreateQuestionReviewTaskAndSubtasksErrorCode.ItemSetMismatch]: {
    title: 'Item Set Mismatch',
    description:
      'Item Set ต่อไปนี้มีจำนวนคำถามไม่ตรงกับข้อมูลในคลัง\nระบบจะนำออกจากรายการอัตโนมัติ กรุณาเพิ่มคำถามใหม่อีกครั้ง',
    acknowledgeText: 'รับทราบ',
  },
};

const DEFAULT_SUBMISSION_ERROR_CONTENT: QuestionReviewTaskSubmissionErrorModalContent = {
  title: 'Submission Failed',
  description: 'ไม่สามารถสร้างงานได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
  acknowledgeText: 'รับทราบ',
};

function createUniqueQuestionIdsByQuestionGroupId(
  selectedQuestionRows: ReturnType<
    typeof useQuestionReviewTaskCreationSelectedRowsContext
  >['selectedQuestionRows'],
) {
  return selectedQuestionRows.reduce<Record<string, string[]>>((accumulator, row) => {
    if (!row.questionGroupId) {
      return accumulator;
    }

    const currentQuestionIds = accumulator[row.questionGroupId] ?? [];
    const nextQuestionIds = row.questionIds ?? [row.id];

    accumulator[row.questionGroupId] = Array.from(
      new Set([...currentQuestionIds, ...nextQuestionIds]),
    );

    return accumulator;
  }, {});
}

export default function ConfirmAndAddAssigneeButton() {
  const [isConfirmModalOpen, confirmModalHandler] = useDisclosure();
  const [isSubmissionErrorModalOpen, submissionErrorModalHandler] = useDisclosure();
  const [submissionErrorContent, setSubmissionErrorContent] =
    useState<QuestionReviewTaskSubmissionErrorModalContent>(DEFAULT_SUBMISSION_ERROR_CONTENT);
  const [mismatchedQuestionIds, setMismatchedQuestionIds] = useState<string[]>([]);
  const {
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { isValid },
  } = useQuestionReviewTaskCreationFormContext();
  const { selectedQuestionRows } = useQuestionReviewTaskCreationSelectedRowsContext();
  const { showSnackbar } = useSnackbar();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const logger = useLogger();

  const [createQuestionReviewTask, { loading: isLoading }] = useCreateQuestionReviewTaskMutation({
    onCompleted: (data) => {
      confirmModalHandler.close();

      const result = data.wflCreateQuestionReviewTask;

      if (!result.success) {
        const baseContent =
          (result.code && SUBMISSION_ERROR_CONTENT_MAP[result.code]) ||
          DEFAULT_SUBMISSION_ERROR_CONTENT;

        const uniqueQuestionIdsByQuestionGroupId =
          createUniqueQuestionIdsByQuestionGroupId(selectedQuestionRows);
        const mismatchQuestionGroupIds = new Set(
          result.itemSetMismatches.map((itemSetMismatch) => itemSetMismatch.id),
        );

        const mismatchQuestionIdsToDelete =
          result.code === CreateQuestionReviewTaskAndSubtasksErrorCode.ItemSetMismatch
            ? Array.from(
                mismatchQuestionGroupIds,
                (questionGroupId) => uniqueQuestionIdsByQuestionGroupId[questionGroupId] ?? [],
              ).flat()
            : [];

        setMismatchedQuestionIds(mismatchQuestionIdsToDelete);

        const submissionErrorItems =
          result.code === CreateQuestionReviewTaskAndSubtasksErrorCode.ItemSetMismatch
            ? result.itemSetMismatches.map((itemSetMismatch) => ({
                id: itemSetMismatch.id,
                label: `Item Set ${
                  (uniqueQuestionIdsByQuestionGroupId[itemSetMismatch.id] ?? []).length
                }Q: ${itemSetMismatch.name}`,
              }))
            : undefined;

        setSubmissionErrorContent({
          ...baseContent,
          items: submissionErrorItems,
        });
        submissionErrorModalHandler.open();
        return;
      }

      showSnackbar({ message: 'Task created successfully', variant: 'success' });

      // Navigate to the created task
      navigate('/task-management/question-review/:taskId', {
        params: { taskId: result?.task?.id || '' },
      });
    },
    onError: (error) => {
      logger.error('Task creation failed:', error);
      confirmModalHandler.close();
      setMismatchedQuestionIds([]);
      setSubmissionErrorContent(DEFAULT_SUBMISSION_ERROR_CONTENT);
      submissionErrorModalHandler.open();
    },
  });

  const handleCreateQSetReviewTask = async () => {
    const { taskName, question, courseId } = getValues();

    const questionIdList = Object.entries(question?.questionId ?? {})
      .filter(([, value]) => value)
      .map(([key]) => key);

    const [code, ...name] = courseId.label.split(':');
    const qbsName = name.join(':').trim();

    const inputVariables = {
      name: taskName,
      assigneeId: userProfile?.id ?? '',
      assignerId: userProfile?.id ?? '',
      ownerId: userProfile?.id ?? '',
      dueDate: null,
      entityId: questionIdList,
      entityType: EntityType.Question,
      subtasks: [],
      workflowCode: WorkflowCode.QuestionReview,
      metadata: {
        qBankStructure: {
          id: courseId.value,
          name: qbsName,
          code: code,
        },
      },
    };

    await createQuestionReviewTask({ variables: { input: inputVariables } });
  };

  const shouldDisableButton =
    !isValid || Object.keys(getValues().question?.questionId ?? {}).length === 0;

  const handleCloseSubmissionErrorModal = () => {
    if (mismatchedQuestionIds.length > 0) {
      const currentQuestionIdMap = getValues().question?.questionId ?? {};
      const nextQuestionIdMap = removeItemSetQuestionIds(
        currentQuestionIdMap,
        mismatchedQuestionIds,
      );

      setValue('question', { questionId: nextQuestionIdMap });
      setMismatchedQuestionIds([]);
    }

    submissionErrorModalHandler.close();
  };

  return (
    <>
      <Flex
        sx={(theme) => ({ flexDirection: 'row', justifyContent: 'end', gap: theme.spacing(4) })}
      >
        <Button
          size='large'
          onClick={() => navigate('/task-management/question-review')}
          variant='outlined'
        >
          Cancel
        </Button>
        <Button
          size='large'
          onClick={isValid ? confirmModalHandler.open : () => trigger()}
          loading={isLoading}
          disabled={shouldDisableButton}
        >
          Save & Add Reviewer
        </Button>
      </Flex>
      <ConfirmationModal
        title='Confirm Task Details?'
        content={
          <>
            ยืนยันรายการของคำถามที่ต้องการให้ทบทวน
            <br />
            เมื่อยืนยันแล้วจะไม่สามารถแก้ไขรายการคำถามได้
          </>
        }
        acceptText='Confirm'
        open={isConfirmModalOpen}
        onAccept={handleSubmit(handleCreateQSetReviewTask)}
        onDiscard={confirmModalHandler.close}
        onClose={confirmModalHandler.close}
      />
      <QuestionReviewTaskSubmissionErrorModal
        open={isSubmissionErrorModalOpen}
        onClose={handleCloseSubmissionErrorModal}
        content={submissionErrorContent}
      />
    </>
  );
}
